import type { Ref, RefCallback, RefObject } from "react";

/**
 * Merges multiple refs into a single ref callback.
 *
 * This utility is essential for components that need to handle both:
 * - React Hook Form's internal ref (from register)
 * - A forwarded ref from the parent component
 *
 * It properly handles both callback refs and RefObject refs, ensuring
 * that both the form library and parent components can access the DOM element.
 *
 * @example
 * ```tsx
 * interface MyInputProps {
 *   name: string;
 *   ref?: Ref<HTMLInputElement>;
 * }
 *
 * function MyInput({ name, ref, ...props }: MyInputProps) {
 *   const { register } = useFormContext();
 *   const { ref: rhfRef, ...rhfRest } = register(name);
 *
 *   return (
 *     <input
 *       {...rhfRest}
 *       {...props}
 *       ref={mergeRefs(rhfRef, ref)}
 *     />
 *   );
 * }
 * ```
 *
 * @param refs - Variable number of refs to merge (can be callback refs or RefObjects)
 * @returns A single ref callback that calls all provided refs
 */
export function mergeRefs<T = any>(
  ...refs: Array<Ref<T> | undefined>
): RefCallback<T> {
  return (value: T) => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === "function") {
        // Callback ref - call it with the value
        ref(value);
      } else {
        // RefObject - set its current property
        (ref as RefObject<T>).current = value;
      }
    });
  };
}

export default mergeRefs;
